import {
  isArray,
  isBoolean,
  isEmpty,
  isNull,
  isNumber,
  isObjectLike,
  isString,
  isUndefined,
} from 'lodash';
import { tr } from '../i18n/i18n';
import moment from 'moment';
import { FORM_VALIDATOR_TYPES } from '../constants';
import { CLASSVEEW_ID_REGEX } from '../constant/auth.constant';
import { IFormField } from '../interface/main.interface';
import parsePhoneNumberFromString from 'libphonenumber-js';

const formValidator = async (data: Record<string, any>): Promise<[any, boolean]> => {
  let isInvalid = false;
  let validatedData = data;
  for await (const [field, fieldData] of Object.entries(data)) {
    let error: string | null = null;
    switch (fieldData.validator) {
      case FORM_VALIDATOR_TYPES.GOOGLE_LINK: {
        const [isError, errorMessage] = await validateGoogleLink(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;
        validatedData = {
          ...validatedData,
          [field]: {
            ...(fieldData as Record<string, any>),
            error,
          },
        };
        break;
      }

      case FORM_VALIDATOR_TYPES.TEXT: {
        const [isError, errorMessage] = await validateText(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;
        validatedData = {
          ...validatedData,
          [field]: {
            ...(fieldData as Record<string, any>),
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.PHONE: {
        const [isError, errorMessage] = await validatePhone(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;
        validatedData = {
          ...validatedData,
          [field]: {
            ...(fieldData as Record<string, any>),
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.NUMBER: {
        const [isError, errorMessage] = await validateNumber(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;

        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.DATE: {
        const [isError, errorMessage] = await validateDate(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;

        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }

      case FORM_VALIDATOR_TYPES.BOOLEAN: {
        const [isError, errorMessage] = await validateBool(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;

        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.ARRAY: {
        const [isError, errorMessage] = await validateArray(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;
        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.PASSWORD: {
        const [isError, errorMessage] = await validatePassword(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;
        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.EMAIL: {
        const [isError, errorMessage] = await validateEmail(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;

        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.EMAIL_OR_CLASSVEEW_ID: {
        const [isError, errorMessage] = await validateEmailOrClassveewId(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;

        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.OBJECT: {
        const [isError, errorMessage] = await validateObject(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;
        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      case FORM_VALIDATOR_TYPES.TIME: {
        const [isError, errorMessage] = await validateTime(fieldData);
        if (isError) isInvalid = true;
        error = errorMessage;
        validatedData = {
          ...validatedData,
          [field]: {
            ...fieldData,
            error,
          },
        };
        break;
      }
      default:
        validatedData = {
          ...validatedData,
          [field]: {
            ...(fieldData as Record<string, any>),
            error,
          },
        };
    }
  }
  return [validatedData, !isInvalid];
};

const validatePhone = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  const invalidMessage = tr('validation.invalid_phone', 'Please enter a valid phone number.');
  const requiredMessage = tr('g.this_field_is_required', 'This field is required.');

  return new Promise((resolve) => {
    // Check if required field is empty
    if (data.isRequired && isEmpty(data.value)) {
      resolve([true, requiredMessage]);
      return;
    }

    // Skip validation if not required and empty
    if (!data.isRequired && isEmpty(data.value)) {
      resolve([false, null]);
      return;
    }

    // Check if value is string
    if (!isString(data.value)) {
      resolve([true, invalidMessage]);
      return;
    }

    // Validate phone format using libphonenumber-js
    const normalizedPhone = data.value.startsWith('+') ? data.value : `+${data.value}`;
    const parsed = parsePhoneNumberFromString(normalizedPhone);

    if (!parsed || !parsed.isValid()) {
      resolve([
        true,
        tr(
          'g.please_enter_a_valid_international_phone_number',
          'Please enter a valid international phone number.',
        ),
      ]);
    } else {
      resolve([false, null]); // Valid phone number
    }
  });
};

const validateGoogleLink = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  const invalidMessage = tr(
    'validation.invalid_google_maps_link',
    'Please enter a valid Google Maps link.',
  );
  const requiredMessage = tr('g.this_field_is_required', 'This field is required.');

  return new Promise((resolve) => {
    if (data.isRequired && isEmpty(data.value)) {
      resolve([true, requiredMessage]);
    }
    const regex =
      /https?:\/\/(?:www\.)?google\.[a-z.]+\/maps(?:\/place\/[^/]+\/)?(?:@([-+]?\d*\.?\d+),([-+]?\d*\.?\d+))?|https?:\/\/(?:www\.)?(?:google\.com\/maps\?q=|maps\.google\.com\/\?q=)([-+]?\d*\.?\d+),([-+]?\d*\.?\d+)/;

    if (data.value) {
      if (!regex.test(data.value) || !isString(data.value)) {
        resolve([true, invalidMessage]);
      }
    }
    resolve([false, null]);
  });
};

const validateText = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_text', 'Invalid text.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');
    const maxLengthMessage = (max: number) =>
      tr('validation.text_max_length', 'Text cannot exceed {{max}} characters.', { max });

    if (data.isRequired && isEmpty(data.value)) {
      resolve([true, requiredMessage]);
    }

    if (data.value && !isString(data.value)) {
      resolve([true, invalidMessage]);
    }

    // Only treat whitespace-only values as error if field is required
    if (data.isRequired && /^\s+$/.test(data.value)) {
      resolve([true, requiredMessage]);
    }

    // Check maxLength if provided
    if (data.maxLength && data.value && data.value.length > data.maxLength) {
      resolve([true, maxLengthMessage(data.maxLength)]);
    }

    resolve([false, null]);
  });
};

const validateNumber = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_number', 'Invalid number.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');

    // Check if required field is empty (null, undefined, or empty string)
    if (data.isRequired && (isNull(data.value) || isUndefined(data.value) || data.value === '')) {
      resolve([true, requiredMessage]);
    }

    // Skip validation if value is null, undefined, or empty string (for optional fields)
    if (isNull(data.value) || isUndefined(data.value) || data.value === '') {
      resolve([false, null]);
    }

    // Validate that the value is a number (including 0)
    if (!isNumber(data.value) && data.value !== 0) {
      resolve([true, invalidMessage]);
    }

    resolve([false, null]);
  });
};

const validateDate = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_date', 'Invalid date.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');
    if (data.isRequired && data.value === null) {
      resolve([true, requiredMessage]);
    }

    if (data.value !== null && !moment(data.value).isValid()) {
      resolve([true, invalidMessage]);
    }

    resolve([false, null]);
  });
};

/**
 * Validates a time string in HH:mm or HH:mm:ss format.
 * Returns [true, errorMessage] if invalid, [false, null] if valid.
 *
 * Usage:
 *   Used in formValidator for FORM_VALIDATOR_TYPES.TIME validation.
 *   Currently used in TeacherAttendanceCutoffForm (src/pages/timeSetting/TeacherAttendance/Form.tsx)
 *   to validate the cutoff time input field.
 *
 * @param data - IFormField<any> containing the time value and validation metadata
 * @returns Promise<[boolean, string | null]> - validation result and error message
 */
const validateTime = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_time', 'Invalid time.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');
    if (data.isRequired && (isNull(data.value) || isUndefined(data.value) || data.value === '')) {
      resolve([true, requiredMessage]);
    }
    // Accepts HH:mm or HH:mm:ss format
    if (
      typeof data.value === 'string' &&
      !/^([0-1]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(data.value)
    ) {
      resolve([true, invalidMessage]);
    }
    resolve([false, null]);
  });
};

const validateBool = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_boolean', 'Invalid boolean.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');
    if (data.isRequired && (isNull(data.value) || isUndefined(data.value))) {
      resolve([true, requiredMessage]);
    }

    if (!isBoolean(data.value)) {
      resolve([true, invalidMessage]);
    }

    resolve([false, null]);
  });
};

const validateArray = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_array', 'Invalid array.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');
    if (
      data.isRequired &&
      (isNull(data.value) || isUndefined(data.value) || data.value.length === 0)
    ) {
      resolve([true, requiredMessage]);
    }

    if (!isArray(data.value)) {
      resolve([true, invalidMessage]);
    }

    resolve([false, null]);
  });
};

/**
 * Pure email-format check shared by the EMAIL and EMAIL_OR_CLASSVEEW_ID
 * validators. Returns true when the string looks like a valid email address.
 */
const isValidEmailFormat = (value: string): boolean => {
  const lastAtPos = value.lastIndexOf('@');
  const lastDotPos = value.lastIndexOf('.');
  return (
    lastAtPos < lastDotPos &&
    lastAtPos > 0 &&
    !value.includes('@@') &&
    lastDotPos > 2 &&
    value.length - lastDotPos > 2
  );
};

const validateEmail = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_email', 'Invalid email.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');

    // Empty value: required → error, optional → pass (allows optional email fields).
    if (isEmpty(data.value)) {
      resolve(data.isRequired ? [true, requiredMessage] : [false, null]);
      return;
    }

    if (!isString(data.value)) {
      resolve([true, invalidMessage]);
    } else if (!isValidEmailFormat(data.value)) {
      resolve([true, invalidMessage]);
    }

    resolve([false, null]);
  });
};

/**
 * Validates the login identifier, which may be EITHER a staff email address
 * OR a student's ClassVeew ID (16 lowercase hex chars — see CLASSVEEW_ID_REGEX).
 * The login screen lowercases the identifier on submit, so we normalise here
 * too before matching the lowercase-hex ID pattern.
 */
const validateEmailOrClassveewId = async (
  data: IFormField<any>,
): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr(
      'validation.invalid_email_or_classveew_id',
      'Please enter a valid email or ClassVeew ID.',
    );
    const requiredMessage = tr('common.field_required', 'This field is required.');

    if (isEmpty(data.value)) {
      resolve(data.isRequired ? [true, requiredMessage] : [false, null]);
      return;
    }

    if (!isString(data.value)) {
      resolve([true, invalidMessage]);
      return;
    }

    const normalized = data.value.trim().toLowerCase();
    if (CLASSVEEW_ID_REGEX.test(normalized) || isValidEmailFormat(normalized)) {
      resolve([false, null]);
      return;
    }

    resolve([true, invalidMessage]);
  });
};

const validateObject = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  return await new Promise((resolve) => {
    const invalidMessage = tr('validation.invalid_object', 'Invalid object.');
    const requiredMessage = tr('g.this_field_is_required', 'This field is required.');
    if (
      data.isRequired &&
      (isNull(data.value) || isUndefined(data.value) || Object.keys(data.value).length === 0)
    ) {
      resolve([true, requiredMessage]);
    }

    if (data.value && (!isObjectLike(data.value) || data.value.constructor !== Object)) {
      resolve([true, invalidMessage]);
    }

    resolve([false, null]);
  });
};

const validatePassword = async (data: IFormField<any>): Promise<[boolean, string | null]> => {
  const requiredMessage = tr('g.this_field_is_required', 'This field is required.');
  if (data.isRequired && isEmpty(data.value)) {
    return [true, requiredMessage];
  }
  const password = data.value || '';
  if (password.length < 8) {
    return [
      true,
      tr(
        'g.password_must_be_at_least_8_characters_long',
        'Password must be at least 8 characters long.',
      ),
    ];
  }
  if (!/[A-Z]/.test(password)) {
    return [
      true,
      tr(
        'g.password_must_contain_at_least_one_uppercase_letter',
        'Password must contain at least one uppercase letter.',
      ),
    ];
  }
  if (!/[0-9]/.test(password)) {
    return [
      true,
      tr(
        'g.password_must_contain_at_least_one_number',
        'Password must contain at least one number.',
      ),
    ];
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return [
      true,
      tr(
        'g.password_must_contain_at_least_one_special_character',
        'Password must contain at least one special character.',
      ),
    ];
  }
  return [false, null];
};

export default formValidator;
